import React, { useEffect, useState } from 'react'
import { Button, CircularProgress } from '@material-ui/core'
import { FieldTypes, userTypes } from '../../utils/constants'
import useBuildingsApi from '../../hooks/buildings/useBuildingsApi'
import useApartmentsApi from '../../hooks/apartments/useApartmentsApi'
import useUsersApi from '../../hooks/users/useUsersApi'
import useToastContext from '../../hooks/useToastContext'
import { notificationTypes } from '../../contexts/toastContext'
import Form from '../common/Form'
import ListHeader from '../common/ListHeader'
import useStyles from '../common/FormFields/FormFieldsStyle'
import ApartmentAssignmentList from './ApartmentAssignmentList'
import ManageApartmentsModal from './ManageApartmentsModal'

export default function UserForm({ onSubmit, handleCancel, user, building }) {
  const classes = useStyles()
  const addToast = useToastContext()
  const editing = user && user.id

  // Initialize apartment assignments from existing user data.
  // These will be replaced by a fresh fetch below when editing, to ensure we
  // have ALL apartments even if the list was building-filtered.
  const [apartmentAssignments, setApartmentAssignments] = useState(() => {
    if (user?.apartments && user.apartments.length > 0) {
      return user.apartments.map((apt) => ({
        building: apt.building || null,
        apartment: apt,
      }))
    }
    if (building) {
      return [{ building, apartment: null }]
    }
    return [{ building: null, apartment: null }]
  })

  const [values, setValues] = useState(
    user || {
      enabled: true,
      doorbellNotificationsEnabled: true,
      openDoorNotificationsEnabled: true,
    }
  )

  const userBuilding =
    user?.apartments && user?.apartments?.length > 0
      ? user?.apartments[0]?.building
      : building
  const [selectedBuilding, setSelectedBuilding] = useState(userBuilding || null)
  const [selectedBuildings, setSelectedBuildings] = useState(
    user?.buildings || []
  )
  const [manageApartmentsOpen, setManageApartmentsOpen] = useState(false)
  const [isLoadingApartments, setIsLoadingApartments] = useState(
    !!(editing && user.type === userTypes.user)
  )

  const [{ buildingsState }, { getBuildings }] = useBuildingsApi()
  const [{ getApartmentsFromBuildingState }, { getApartmentsFromBuilding }] =
    useApartmentsApi()
  const [, { getUserApartments }] = useUsersApi()

  useEffect(() => {
    getBuildings()

    // When editing, re-fetch the user's full apartment list so we don't
    // accidentally drop cross-building assignments that were filtered out
    // in the list view.
    if (editing && user.type === userTypes.user) {
      getUserApartments(user.id)
        .then((apartments) => {
          if (apartments && apartments.length > 0) {
            setApartmentAssignments(
              apartments.map((apt) => ({
                building: apt.building || null,
                apartment: apt,
              }))
            )
          }
        })
        .catch(() => {
          addToast(notificationTypes.error, 'Error al cargar las unidades del usuario')
        })
        .finally(() => setIsLoadingApartments(false))
    }
  }, [])

  useEffect(() => {
    if (selectedBuilding) {
      const fetchApartmentsFromBuilding = async () => {
        getApartmentsFromBuilding(selectedBuilding.id)
      }
      fetchApartmentsFromBuilding()
    }
  }, [selectedBuilding && selectedBuilding.id])

  return (
    <div className={classes.paper} style={{ paddingRight: '84px' }}>
      <ListHeader
        bigTitle={`${user && user.id ? 'Editar' : 'Nuevo'} usuario`}
        canCreate={false}
      />
      <Form
        values={values}
        setValues={setValues}
        fields={[
          {
            type: FieldTypes.TextField,
            label: 'Nombre',
            hint: 'Ingrese un nombre',
            key: 'name',
            errorMessage: 'El nombre es requerido',
            onlyLetters: true,
            capitalized: true,
          },
          {
            type: FieldTypes.TextField,
            label: 'Apellido',
            hint: 'Ingrese un apellido',
            key: 'surname',
            errorMessage: 'El apellido es requerido',
            onlyLetters: true,
            capitalized: true,
          },
          {
            type: FieldTypes.TextField,
            label: 'Email',
            hint: 'Ingrese un email',
            key: 'email',
            errorMessage: 'El email es requerido',
          },
          {
            type: FieldTypes.TextField,
            label: 'Teléfono',
            hint: 'Ingrese un teléfono',
            key: 'phone',
            numeric: true,
          },
          {
            type: FieldTypes.TextField,
            label: 'Contraseña',
            hint: 'Ingrese una contraseña',
            key: 'password',
            errorMessage: !editing && 'La contraseña es requerida',
          },
          { type: FieldTypes.Switch, label: 'Habilitado', key: 'enabled' },
          {
            type: FieldTypes.Switch,
            label: 'Notificaciones porteros',
            key: 'doorbellNotificationsEnabled',
          },
          {
            type: FieldTypes.Switch,
            label: 'Notificaciones autorizados',
            key: 'openDoorNotificationsEnabled',
          },
          {
            type: FieldTypes.Select,
            id: 'user-type',
            label: 'Tipo de usuario',
            hint: 'Seleccione un tipo',
            key: 'type',
            errorMessage: 'El tipo es requerido',
            withMargin: true,
            options: [
              { value: userTypes.admin, label: 'Administrador' },
              { value: userTypes.operator, label: 'Operador' },
              { value: userTypes.user, label: 'Usuario' },
              {
                value: userTypes.buildingAdmin,
                label: 'Administrador de edificio',
              },
              { value: userTypes.security, label: 'Seguridad' },
              {
                value: userTypes.securityAdmin,
                label: 'Seguridad Administrador',
              },
              {
                value: userTypes.gestor,
                label: 'Gestor',
              },
            ],
          },
          ...(values.type === userTypes.user
            ? [
                {
                  type: FieldTypes.Custom,
                  key: 'apartmentAssignments',
                  render: () => (
                    <div>
                      {isLoadingApartments ? (
                        <div style={{ padding: '16px 0' }}>
                          <CircularProgress size={24} />
                        </div>
                      ) : (
                        <ApartmentAssignmentList
                          assignments={apartmentAssignments}
                          onChange={setApartmentAssignments}
                          buildings={buildingsState.buildings}
                          disableBuilding={!!building}
                          disabled={!!editing}
                        />
                      )}
                      {editing && (
                        <Button
                          variant="outlined"
                          className={classes.secondaryButton}
                          onClick={() => setManageApartmentsOpen(true)}
                          style={{ marginTop: '8px' }}
                        >
                          Gestionar unidades
                        </Button>
                      )}
                    </div>
                  ),
                },
              ]
            : []),
          ...(values.type === userTypes.buildingAdmin ||
          values.type === userTypes.security
            ? [
                {
                  type: FieldTypes.Autocomplete,
                  label: 'Cliente',
                  hint: 'Seleccione un cliente',
                  key: 'building',
                  options: buildingsState.buildings,
                  multiple: false,
                  freeSolo: false,
                  withMargin: true,
                  disabled: !!building,
                  onChange: (value) => {
                    setSelectedBuilding(value)
                  },
                  value: selectedBuilding,
                },
              ]
            : []),
          ...(values.type === userTypes.gestor ||
          values.type === userTypes.securityAdmin
            ? [
                {
                  type: FieldTypes.Autocomplete,
                  label: 'Edificios',
                  hint: 'Seleccione al menos un edificio',
                  key: 'buildings',
                  options: buildingsState.buildings,
                  multiple: true,
                  freeSolo: false,
                  withMargin: true,
                  disabled: !!building,
                  onChange: (value) => {
                    setSelectedBuildings(value)
                  },
                  value: selectedBuilding,
                },
              ]
            : []),
        ]}
        handleSubmit={() => {
          if (values.type === userTypes.user) {
            if (!editing) {
              const validAssignments = apartmentAssignments.filter(
                (a) => a.apartment
              )
              if (validAssignments.length === 0) {
                addToast(notificationTypes.error, 'Seleccione al menos una unidad')
                return
              }
              values.apartmentIds = validAssignments.map((a) => a.apartment.id)
              // Keep apartments populated for optimistic UI update
              values.apartments = validAssignments.map((a) =>
                Object.assign({}, a.apartment, { building: a.building })
              )
            }
          } else if (
            selectedBuilding ||
            (values.apartments && !apartmentAssignments.length)
          ) {
            values.buildingId = selectedBuilding?.id
          } else if (selectedBuildings.length) {
            values.buildingIds = selectedBuildings.map((b) => b.id)
          }
          delete values.image
          onSubmit(values)
        }}
        handleCancel={handleCancel}
      />

      {editing && (
        <ManageApartmentsModal
          open={manageApartmentsOpen}
          onClose={() => setManageApartmentsOpen(false)}
          onSave={(savedAssignments) => {
            setApartmentAssignments(
              savedAssignments.length > 0
                ? savedAssignments
                : [{ building: building || null, apartment: null }]
            )
          }}
          user={user}
          building={building}
          buildings={buildingsState.buildings}
          initialAssignments={apartmentAssignments}
        />
      )}
    </div>
  )
}
